import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MoCxlGroupData,
  MoFlightData,
  MoInfoData,
  MoTableDetailsData,
  MoTablePaxesData,
  MoTablePaymentData,
  MoTableRemarksData,
  MoTableRoomData,
  MyOrder,
  OrderLog,
} from 'ts/shared/interfaces/MOResponse';
import Table from './Table';
import {
  getIcon,
  getMoDetailsTable,
  getStatus,
  moCxlGroup,
  moFlightData,
  moFlightTable,
  moInfoTable,
  moOwnerTable,
  moParamsTable,
  moPaxesTable,
  moPaymentsTable,
  moRemarksTable,
  moRoomTable,
} from 'constants/mo';
import {
  formatCxlGroup,
  formatDetailsGroup,
  formatFlightTable,
  formatInfoGroup,
  formatPaxesTable,
  formatPaymentTable,
  formatRemarksTable,
  formatRoomTable,
  getMoDetails,
} from 'utils/shared/my-order';
import MoAmount from './MoAmount';
import MoGroupData from './MoGroupData';
import { dateFormat, showSuccess } from 'utils/utils';
import MoSupplier from './MoSupplier';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarXmark,
  faCopy,
  faEnvelope,
  faFileLines,
  faHotel,
  faInfo,
  faMoneyBill,
  faPlane,
  faTag,
  faTicket,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { faCcVisa } from '@fortawesome/free-brands-svg-icons';
import MoAddRemark from './MoActions/MoAddRemark';
import MoTicket from './MoTicket';
import withErrorBoundary from '../withErrorBoundary';
import AddPaymentModal from './AddPaymentModal';
import Button from 'components/ui/Button/Button';
import { openLog } from 'services/myOrders';
import MoLogTable from './MoLogTable';
import MoAddPrivateRemark from './MoModal/MoAddPrivateRemark';
import MoCopyOrderJson from './MoActions/MoCopyOrderJson';
import { MainContext } from 'contexts/MainContext';
import Tabs from 'components/ui/Tabs/Tabs';

interface MoMoreDetailsProps {
  activeOrder: MyOrder;
  onUpdateMyOrder: (my: MyOrder) => void;
  onLoadLogs: (log: OrderLog[]) => void;
  activeInitialTab?: number;
  showCopyOrderJson?: boolean;
}

const MoMoreDetails: FC<MoMoreDetailsProps> = ({ activeOrder, onUpdateMyOrder, onLoadLogs, activeInitialTab }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(4);
  const [remarksData, setRemarksData] = useState<MoTableRemarksData[]>([]);
  const [internalRemarksData, setInternalRemarksData] = useState<MoTableRemarksData[]>([]);
  const [paymentsTable, setPaymentsTable] = useState<MoTablePaymentData[]>([]);
  const [detailsGroup, setDetailsGroup] = useState<MoTableDetailsData>();
  const [roomsData, setRoomsData] = useState<MoTableRoomData[]>([]);
  const [paxesTable, setPaxesTable] = useState<MoTablePaxesData[]>([]);
  const [flightData, setFlightData] = useState<MoFlightData[]>([]);
  const [cxlData, setCxlData] = useState<MoCxlGroupData>();
  const [infoData, setInfoData] = useState<MoInfoData>();
  const { user } = useContext(MainContext);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [logs, setLogs] = useState<OrderLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    setActiveTab(activeInitialTab || 0);
  }, [activeInitialTab]);

  useEffect(() => {
    setTimeout(() => {
      const div = document.getElementById(`div-${activeTab}`);
      if (div) {
        div.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [activeTab]);

  useEffect(() => {
    setRemarksData(formatRemarksTable(activeOrder.fullSegment, activeOrder.fullSegment.remarks, onUpdateMyOrder));
    setInternalRemarksData(formatRemarksTable(activeOrder.fullSegment, activeOrder.fullSegment.internalRemarks, onUpdateMyOrder));
    setPaymentsTable(formatPaymentTable(activeOrder.order.paymentTransactionResponses));
    setDetailsGroup(formatDetailsGroup(activeOrder));
    setRoomsData(formatRoomTable(activeOrder.fullSegment));
    setPaxesTable(formatPaxesTable(activeOrder.order.paxes));
    setFlightData(formatFlightTable(activeOrder.fullSegment));
    setCxlData(formatCxlGroup(activeOrder.fullSegment));
    setInfoData(formatInfoGroup(activeOrder));
  }, [activeOrder]); // eslint-disable-line

  const handleLoadLogs = async () => {
    if (!user?.user_group_id || user.user_group_id <= 60) {
      return;
    }

    try {
      setLogsLoading(true);
      const res = await openLog({
        orderId: activeOrder.orderRow.orderId,
        segmentId: activeOrder.orderRow.segmentId,
      });

      if (res?.orderLogs) {
        setLogs(res.orderLogs);
        onLoadLogs(res.orderLogs);
      }
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <div className="mo-tabs-left">
      <Tabs
        size="small"
        label={t('mo.segment_full')}
        value={String(activeTab)}
        onValueChange={(v) => setActiveTab(Number(v))}
        className="text-sm"
        items={[
          {
            value: '4',
            label: <FontAwesomeIcon icon={getIcon(activeOrder.fullSegment.productType)} />,
          },
          ...(activeOrder.fullSegment.rooms.length
            ? [
                {
                  value: '11',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faHotel} />
                      {t('hotel.rooms')}
                    </>
                  ),
                },
                {
                  value: '8',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faCalendarXmark} />
                      CXL -{' '}
                      <span
                        className={
                          new Date(activeOrder.fullSegment.rooms[0].cancellationDateLimit) > new Date()
                            ? 'text-success'
                            : 'color-danger'
                        }
                      >
                        {dateFormat(activeOrder.fullSegment.rooms[0].cancellationDateLimit, 'dd-MM-yyyy')}
                      </span>
                    </>
                  ),
                },
              ]
            : []),
          ...(activeOrder.fullSegment.details.length
            ? [
                {
                  value: '5',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faInfo} />
                      Details
                    </>
                  ),
                },
              ]
            : []),
          ...(user?.branch?.hotelDisplaySupplierName
            ? [
                {
                  value: '9',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faTag} />
                      Supplier
                      <span className="text-success">
                        {' '}-{' '}
                        <span style={{ textTransform: 'capitalize' }}>
                          {activeOrder.fullSegment.sysSuppDesc?.toLowerCase()?.split(' ')?.[0]}
                        </span>
                      </span>
                    </>
                  ),
                },
              ]
            : []),
          {
            value: '6',
            label: (
              <>
                <FontAwesomeIcon icon={faMoneyBill} />
                {t('mo.amount')}
              </>
            ),
          },
          {
            value: '0',
            label: (
              <>
                <FontAwesomeIcon icon={faEnvelope} />
                {t('mo.remarks')} ({remarksData.length})
              </>
            ),
          },
          {
            value: '7',
            label: (
              <>
                <FontAwesomeIcon icon={faUsers} />
                Paxes
              </>
            ),
          },
          {
            value: '3',
            label: (
              <>
                <FontAwesomeIcon icon={faCcVisa} />
                {t('mo.payments')} ({paymentsTable.length})
              </>
            ),
          },
          {
            value: '1',
            label: (
              <>
                <FontAwesomeIcon icon={faUser} />
                {t('mo.info')}
              </>
            ),
          },
          ...(activeOrder.order.hp_Ticket
            ? [
                {
                  value: '10',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faTicket} />
                      {t('mo.ticket')} <span className="text-contrast">{activeOrder.order.hp_Ticket}</span>
                    </>
                  ),
                },
              ]
            : []),
          ...(user?.user_group_id && user.user_group_id > 60
            ? [
                {
                  value: '12',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faFileLines} />
                      {t('mo.logs')}
                    </>
                  ),
                },
                {
                  value: '13',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faCopy} />
                      Actions
                    </>
                  ),
                },
              ]
            : []),
        ]}
      />
      <div className="mo-tabs-content-wr">
        <div className="mo-tabs-content">
          {!!detailsGroup && (
            <div id="div-4" className='default-shadow details-block'>
              <div className="title title-header-grey">
                <div className="text-bold mb-5">{activeOrder.fullSegment.itemDesc}
                  <FontAwesomeIcon
                    icon={faCopy}
                    size="xs"
                    color="var(--color-warning)"
                    className="ml-5 cur-poi"
                    onClick={() => {
                      navigator.clipboard.writeText(`${activeOrder.fullSegment.itemDesc}`);
                      showSuccess(t('mo.clipboard_success'));
                    }}
                  />
                </div>
              </div>
              <MoGroupData
                mainClassName="mo-group-wr-3x"
                data={detailsGroup}
                columns={getMoDetailsTable(activeOrder.fullSegment.productType)}
                columnsCount={3}
                columnWidth={110}
              />
            </div>
          )}
          {!!roomsData.length && (
            <div id="div-11" className='default-shadow details-block'>
              <div className="title title-header-grey">
                <div className="text-bold mb-5">{t('hotel.rooms')}</div>
              </div>
              <Table data={roomsData} columns={moRoomTable} overflow="hidden" />
            </div>
          )}
          {!!flightData.length && (
            <div id="div-5" className='default-shadow details-block'>
              <div className="title title-header-grey">
                <div className="text-bold mb-5">Details</div>
              </div>
              <Table data={flightData} columns={moFlightTable} />
              <div className="mb-20"></div>
              {activeOrder.fullSegment.details.map((detail, index) => (
                <div key={index}>
                  <div className="mo-tabs-row mb-10 mt-20 text-bold text-sm display-flex">
                    {detail.productTypeCode === 'FLT' && (
                      <span>
                        #{detail.orderSegDetailId}
                        <FontAwesomeIcon
                          icon={faPlane}
                          className="ph-10"
                          size="sm"
                          transform={{ rotate: -45 }}
                        />
                        <span className="ml-10">{detail.item.name}</span>
                      </span>
                    )}
                    {detail.productTypeCode === 'SACT' && <span>{detail.item.name}</span>}
                    {detail.productTypeCode === 'HTL' && (
                      <div>
                        <div>
                          #{detail.orderSegDetailId}{' '}
                          <span className="ph-10">{detail.item.name}</span>
                        </div>
                        <div>
                          Room: {detail.room.name} [Board: {detail.board.code}]
                        </div>
                        <div>
                          Paxes:{' '}
                          {activeOrder.order.paxes
                            .map((p) => `${p.firstName} ${p.lastName}`)
                            .join(', ')}
                        </div>
                      </div>
                    )}

                    <span className="ml-a">
                      <span className="ph-10">Paxes: {detail.paxes.length}</span>
                      <span className="pl-10">
                        <span className={`mo-status ${getStatus(detail.sysStatus.code)}`}>
                          {detail.sysStatus.code}
                        </span>
                      </span>
                    </span>
                  </div>
                  <MoGroupData
                    mainClassName="mo-group-wr-3x"
                    data={getMoDetails(detail)}
                    columns={moFlightData}
                    columnsCount={3}
                    columnWidth={110}
                  />
                </div>
              ))}
            </div>
          )}
          {!!activeOrder.fullSegment.rooms.length && !!roomsData.length && (
            <div className='default-shadow details-block' id="div-8">
              <div className="title title-header-grey">
                <span className="text-bold mb-5 display-ib mr-5">CXL  - </span>
                <span className={
                  new Date(activeOrder.fullSegment.rooms?.[0]?.cancellationDateLimit) > new Date()
                    ? 'text-success'
                    : 'color-danger'
                }>
                  {dateFormat(activeOrder.fullSegment.rooms[0].cancellationDateLimit, 'dd-MM-yyyy')}
                </span>
              </div>

              <div
                className={
                  new Date(activeOrder.fullSegment.rooms?.[0]?.cancellationDateLimit) > new Date()
                    ? ''
                    : 'mo-group-cxl'
                }
              >
                <div className="flex-between mo-tabs-row mb-20">
                  <span className="text-bold">{roomsData[0].room}</span>
                  <span>{roomsData[0].paxStr}</span>
                  <span className={`mo-status ml-4 ${getStatus(roomsData[0].status)}`}>
                    {roomsData[0].status}
                  </span>
                </div>

                <MoGroupData
                  columns={moCxlGroup}
                  data={cxlData}
                  columnsCount={1}
                  mainClassName="mo-group-wr-1x"
                />
              </div>
            </div>
          )}

          {!!user?.branch?.hotelDisplaySupplierName && (
            <div id="div-9" className='default-shadow details-block'>
              <div className="title title-header-grey">
                <div className="text-bold mb-5">Supplier

                  <span className="text-success">
                    {' '}-{' '}
                    <span style={{ textTransform: 'capitalize' }}>{activeOrder.fullSegment.sysSuppDesc?.toLowerCase()?.split(' ')?.[0]}</span>
                  </span>
                </div>
              </div>
              <MoSupplier myOrder={activeOrder} supplier={activeOrder.suppliersByCode[activeOrder.orderRow.sysSuppCode]} />
            </div>
          )}

          <div id="div-6" className='default-shadow details-block'>
            <div className="title title-header-grey">
              <div className="text-bold mb-5">{t('mo.amount')}</div>
            </div>
            <MoAmount segment={activeOrder.fullSegment} />
          </div>

          <div id="div-0" className='default-shadow details-block'>
            <div className="title title-header-grey">
              <div className="text-bold mb-5">{t('mo.remarks')}</div>
            </div>
            <MoAddRemark orderRow={activeOrder.orderRow} onSuccess={onUpdateMyOrder} />
            <span className='ml-5'></span>

            <Table data={remarksData} columns={moRemarksTable} />
            {user?.branchId === 5000 &&
              <Fragment>
                <div className='divider mb-15 mt-10'></div>
                <MoAddPrivateRemark orderRow={activeOrder.orderRow} onSuccess={onUpdateMyOrder} />
                <Table data={internalRemarksData} columns={moRemarksTable} />
              </Fragment>}
          </div>
          <div className='default-shadow details-block' id="div-7">
            <div className="title title-header-grey">
              <div className="text-bold mb-5">Paxes</div>
            </div>
            <div>
              <Table data={paxesTable} columns={moPaxesTable} />
            </div>
            {activeOrder.fullSegment.productType !== 'FLT' && (
              <MoGroupData
                mainClassName="mo-group-wr-4x"
                columns={moOwnerTable}
                data={activeOrder.order.owner}
                columnsCount={4}
                columnWidth={110}
              />
            )}
            <div className="mb-20"></div>
            <Table data={[activeOrder.order]} columns={moParamsTable} />
          </div>
          <div id="div-3" className='default-shadow details-block'>
            <div className="title title-header-grey">
              <div className="text-bold mb-5">{t('mo.payments')}</div>
            </div>
            {
              user?.branchId === 5000 &&
              <Button title={t('mo.add_payment')}
                className='btn btn-primary btn-ssm mb-10' onClick={() => setShowAddPaymentModal(true)} />
            }

            <div className='mb-20'></div>
            <Table data={paymentsTable} columns={moPaymentsTable} />
          </div>
          <div id="div-1" className='default-shadow details-block'>
            <div className="title title-header-grey">
              <div className="text-bold mb-5">Info</div>
            </div>
            <div className="mo-tabs-row mb-10">
              <div className="text-bold mb-5">
                {activeOrder.orderRow.agentName} | {activeOrder.orderRow.branchName}
              </div>
            </div>
            <MoGroupData
              mainClassName="mo-group-wr-2x mo-group-wr-info"
              columns={moInfoTable}
              data={infoData}
              columnsCount={2}
              columnWidth={110}
            />
          </div>
          {!!activeOrder.order.hp_Ticket && (
            <div id="div-10" className='default-shadow details-block'>
              <div className="title title-header-grey">
                <div className="text-bold mb-5">{t('mo.ticket')}</div>
              </div>
              <MoTicket myOrder={activeOrder} />
            </div>
          )}
        </div>
        {user?.user_group_id && user.user_group_id > 60 && (
          <div id="div-12" className='default-shadow details-block'>
            <div className="title title-header-grey">
              <div className="text-bold mb-5">{t('mo.logs')}</div>
            </div>
            <div className="mb-20">
              <Button
                title={t(logsLoading ? 'mo.loading' : 'Show logs')}
                onClick={handleLoadLogs}
                disabled={logsLoading}
                className="btn btn-primary btn-ssm"
              />
            </div>
            {!!logs.length && <MoLogTable data={logs} />}
          </div>
        )}
        {user?.user_group_id && user.user_group_id > 60 && (
          <div id="div-13" className="default-shadow details-block">
            <div className="title title-header-grey">
              <div className="text-bold mb-5">Actions</div>
            </div>
            <MoCopyOrderJson orderRow={activeOrder.orderRow} />
          </div>
        )}
      </div>
      <AddPaymentModal isOpen={showAddPaymentModal} onClose={() => setShowAddPaymentModal(false)} order={activeOrder} />

    </div>
  );
};

export default withErrorBoundary(MoMoreDetails, 'MoMoreDetails');
